import React from 'react';
import { Chip, IconButton, Stack, Tooltip } from '@mui/material';
import { GridColDef, GridComparatorFn, GridRenderCellParams } from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';

import { WhatsNewComingSoonItem, WhatsNewUpdate } from './types';
import { formatDisplayDateTime, toInstant } from './utils';

/**
 * Timestamps are stored as ISO strings, and the grid's default string compare
 * misorders them once offsets differ (`+05:30` against `Z`). Comparing the
 * parsed instant keeps a header click consistent with the default order.
 */
const byInstant: GridComparatorFn<string | null> = (a, b) => toInstant(a) - toInstant(b);

interface RowActionHandlers<T> {
  // eslint-disable-next-line no-unused-vars -- parameter names document the typed callbacks
  onEdit: (row: T) => void;
  // eslint-disable-next-line no-unused-vars -- parameter names document the typed callbacks
  onDelete: (row: T) => void;
}

const buildActionsColumn = <T extends { id: string }>({
  onEdit,
  onDelete
}: RowActionHandlers<T>): GridColDef => ({
  field: 'actions',
  headerName: 'Actions',
  width: 120,
  sortable: false,
  filterable: false,
  renderCell: (params: GridRenderCellParams) => (
    <Stack direction='row' spacing={0.5}>
      <Tooltip title='Edit'>
        <IconButton size='small' onClick={() => onEdit(params.row as T)}>
          <Edit fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title='Delete'>
        <IconButton size='small' onClick={() => onDelete(params.row as T)}>
          <Delete fontSize='small' />
        </IconButton>
      </Tooltip>
    </Stack>
  )
});

export const buildUpdateColumns = (handlers: RowActionHandlers<WhatsNewUpdate>): GridColDef[] => [
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 220 },
  { field: 'category', headerName: 'Category', flex: 1, minWidth: 130 },
  {
    field: 'is_published',
    headerName: 'Status',
    width: 130,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        size='small'
        label={params.value ? 'Published' : 'Draft'}
        color={params.value ? 'success' : 'default'}
        variant={params.value ? 'filled' : 'outlined'}
      />
    )
  },
  {
    field: 'published_at',
    sortComparator: byInstant,
    headerName: 'Published at',
    flex: 1,
    minWidth: 180,
    valueFormatter: ({ value }) => formatDisplayDateTime(value as string | null)
  },
  {
    field: 'expires_at',
    sortComparator: byInstant,
    headerName: 'Expires at',
    flex: 1,
    minWidth: 180,
    valueFormatter: ({ value }) => formatDisplayDateTime(value as string | null)
  },
  buildActionsColumn(handlers)
];

export const buildComingSoonColumns = (
  handlers: RowActionHandlers<WhatsNewComingSoonItem>
): GridColDef[] => [
  { field: 'title', headerName: 'Title', flex: 2, minWidth: 220 },
  { field: 'category', headerName: 'Category', flex: 1, minWidth: 130 },
  { field: 'planned_for', headerName: 'Planned for', flex: 1, minWidth: 150 },
  buildActionsColumn(handlers)
];
